import { Injectable, signal, computed } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { CustomerProfile } from '../models/customer.model';

@Injectable({ providedIn: 'root' })
export class AppStorageService {
  languagePreference = signal<string>('en');
  customerEmailAddress = signal<string | null>(null);
  accessToken = signal<string | null>(null);
  accessTokenExpiresOn = computed<number>(() => {
    const token = this.accessToken();
    if (!token) return 0;
    try {
      return jwtDecode<{ exp?: number }>(token).exp ?? 0;
    } catch {
      return 0;
    }
  });
  // Derived from the access token's `idp` claim, not client intent — Entra can
  // silently fall back a social-initiated login to the existing OTP account
  // (e.g. email already registered via OTP), so which button the user clicked
  // is not reliable evidence of which identity provider actually authenticated them.
  private identityProvider = computed<string | null>(() => {
    const token = this.accessToken();
    if (!token) return null;
    try {
      return jwtDecode<{ idp?: string }>(token).idp ?? null;
    } catch {
      return null;
    }
  });
  isGoogleSso = computed<boolean>(() => this.identityProvider() === 'google.com');
  isAppleSso = computed<boolean>(
    () => this.identityProvider()?.endsWith('apple.com') ?? false
  );

  private _customerProfile = signal<CustomerProfile | null>(null);
  readonly customerProfile = this._customerProfile.asReadonly();

  setCustomerProfile(profile: CustomerProfile | null): void {
    this._customerProfile.set(profile);
  }

  updateCustomerProfile(partial: Partial<CustomerProfile>): void {
    this._customerProfile.update((profile) => ({ ...profile!, ...partial }));
  }
}
